import React, { useEffect } from "react";
import { MarketingHeader, MarketingFooter } from "@/components/marketing";

const TEAL = "#22D3C5";

export function slugify(s) {
  return String(s)
    .toLowerCase()
    .replace(/[^a-z0-9æøå]+/g, "-")
    .replace(/^-|-$/g, "");
}

export function LegalSection({ title, children }) {
  return (
    <section id={slugify(title)} className="scroll-mt-28 pt-10 first:pt-0">
      <h2 className="font-display text-xl sm:text-2xl font-medium tracking-tight text-balance mb-4">{title}</h2>
      <div className="space-y-4 text-muted-foreground leading-relaxed">{children}</div>
    </section>
  );
}

export function LegalLayout({ eyebrow = "Juridisk", title, updated, intro, children }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const items = React.Children.toArray(children)
    .filter((c) => c && c.props && c.props.title)
    .map((c) => ({ title: c.props.title, id: slugify(c.props.title) }));

  const [activeId, setActiveId] = React.useState(null);
  useEffect(() => {
    const els = items.map((it) => document.getElementById(it.id)).filter(Boolean);
    if (!els.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-96px 0px -70% 0px", threshold: 0 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      <div className="grain" />
      <MarketingHeader />

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-8 pt-14 sm:pt-16 pb-24">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 mb-6">
            <span className="relative inline-flex h-1.5 w-1.5">
              <span className="absolute inset-0 rounded-full aic-pulse-ring" style={{ backgroundColor: TEAL, opacity: 0.4 }} />
              <span className="relative h-1.5 w-1.5 rounded-full" style={{ backgroundColor: TEAL }} />
            </span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{eyebrow}</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-[1.05] text-balance">{title}</h1>
          {updated && <p className="text-sm text-muted-foreground mt-4">Senest opdateret: {updated}</p>}
          {intro && <p className="mt-5 text-muted-foreground leading-relaxed max-w-2xl">{intro}</p>}
        </div>

        <div className="mt-10 h-px bg-border" />

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10 lg:gap-16">
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground/70 mb-4">Indhold</p>
              <nav className="border-l border-border">
                {items.map((it) => {
                  const active = activeId === it.id;
                  return (
                    <a key={it.id} href={`#${it.id}`}
                      className="block pl-4 -ml-px border-l py-1.5 text-[13px] transition-colors"
                      style={active
                        ? { color: TEAL, borderColor: TEAL }
                        : { borderColor: "transparent" }}
                      data-active={active ? "true" : "false"}
                    >
                      <span className={active ? "" : "text-muted-foreground hover:text-foreground"}>{it.title}</span>
                    </a>
                  );
                })}
              </nav>
            </div>
          </aside>
          <div className="max-w-2xl">{children}</div>
        </div>
      </div>

      <MarketingFooter />
    </div>
  );
}
